import React from 'react';
import LegalPage from './LegalPage';

export default function RGPD() {
  return (
    <LegalPage
      title="Politique de confidentialité"
      subtitle="Protection des données personnelles — RGPD & loi algérienne 18-07"
      lastUpdate="31 mai 2026"
      testid="page-rgpd"
    >
      <h2>1. Introduction</h2>
      <p>
        La protection de vos données personnelles est une priorité pour <strong>TripNova SaaS</strong>.
        La présente Politique de confidentialité explique quelles données nous collectons,
        pourquoi, comment nous les traitons, et quels sont vos droits.
      </p>
      <p>
        Elle est conforme aux exigences :
      </p>
      <ul>
        <li>Du <strong>Règlement Général sur la Protection des Données</strong> (RGPD, UE 2016/679) pour les utilisateurs européens.</li>
        <li>De la <strong>loi algérienne n° 18-07</strong> du 10 juin 2018 relative à la protection des personnes physiques dans le traitement des données à caractère personnel.</li>
      </ul>

      <h2>2. Responsable du traitement</h2>
      <p>
        Le responsable du traitement est l'éditeur de TripNova SaaS, joignable à l'adresse :{' '}
        <a href="mailto:aatvgeste@gmail.com">aatvgeste@gmail.com</a>.
      </p>

      <h2>3. Données collectées</h2>

      <h3>3.1 Données des utilisateurs (chefs d'agence, agents)</h3>
      <ul>
        <li>Identité : nom, prénom, téléphone, email.</li>
        <li>Données d'authentification : mot de passe (haché en bcrypt — jamais en clair).</li>
        <li>Données de connexion : adresse IP, navigateur, dates de connexion.</li>
        <li>Données d'usage : pages visitées, actions effectuées (audit log).</li>
      </ul>

      <h3>3.2 Données des clients de l'agence (saisies par l'agence)</h3>
      <ul>
        <li>Identité : nom, prénom, téléphone, email, adresse.</li>
        <li>Données passeport (si activé) : numéro, nationalité, date d'expiration.</li>
        <li>Données financières : montants payés, méthodes de paiement, soldes.</li>
        <li>Données de voyage : destinations, dates, hôtels, billets.</li>
      </ul>
      <p>
        <em>Note importante :</em> ces données sont collectées par l'Agence dans le cadre de son
        activité commerciale. TripNova agit ici en tant que <strong>sous-traitant</strong> au sens du RGPD.
      </p>

      <h3>3.3 Données techniques (Real User Monitoring)</h3>
      <ul>
        <li>Métriques de performance (Core Web Vitals) : temps de chargement, réactivité.</li>
        <li>User-Agent (navigateur, OS).</li>
        <li>IP <strong>anonymisée</strong> (hachée, jamais stockée en clair pour le RUM).</li>
      </ul>

      <h2>4. Finalités du traitement</h2>
      <p>Vos données sont traitées pour les finalités suivantes :</p>
      <ul>
        <li><strong>Fournir le Service</strong> : gestion des ventes, voyages, clients, paiements.</li>
        <li><strong>Sécurité</strong> : authentification, détection de fraude, rate limiting, audit logs.</li>
        <li><strong>Communication</strong> : envoi d'emails transactionnels (factures, vouchers, rappels).</li>
        <li><strong>Amélioration du Service</strong> : analyse anonymisée des performances et erreurs.</li>
        <li><strong>Respect des obligations légales</strong> : facturation, comptabilité, lutte anti-fraude.</li>
      </ul>

      <h2>5. Base légale du traitement</h2>
      <ul>
        <li><strong>Exécution du contrat</strong> (CGU) pour la fourniture du Service.</li>
        <li><strong>Obligation légale</strong> pour les données comptables et fiscales.</li>
        <li><strong>Intérêt légitime</strong> pour la sécurité, la lutte anti-fraude et l'amélioration du Service.</li>
        <li><strong>Consentement</strong> pour les communications non transactionnelles (newsletters, marketing).</li>
      </ul>

      <h2>6. Durée de conservation</h2>
      <ul>
        <li>Données de compte actif : durée de l'abonnement + 30 jours.</li>
        <li>Données comptables (factures, paiements) : <strong>10 ans</strong> (obligation légale).</li>
        <li>Audit logs de sécurité : <strong>3 ans</strong>.</li>
        <li>Métriques RUM anonymisées : <strong>90 jours</strong>.</li>
        <li>Cookies d'authentification : durée de la session (max 7 jours).</li>
      </ul>

      <h2>7. Destinataires des données</h2>
      <p>Vos données ne sont jamais vendues. Elles peuvent être partagées avec :</p>
      <ul>
        <li>Les <strong>collaborateurs habilités</strong> de votre Agence (multi-tenant strict — aucune fuite vers d'autres agences).</li>
        <li>Nos <strong>sous-traitants techniques</strong> :
          <ul>
            <li>Hébergement et infrastructure cloud (chiffré).</li>
            <li>Service d'email transactionnel (Resend).</li>
            <li>Monitoring d'erreurs (Sentry — données anonymisées).</li>
            <li>Cache Redis (Upstash — données techniques uniquement).</li>
          </ul>
        </li>
        <li>Les <strong>autorités compétentes</strong> en cas d'obligation légale.</li>
      </ul>

      <h2>8. Transferts hors UE / Algérie</h2>
      <p>
        Certains sous-traitants techniques peuvent être basés hors de l'Union européenne ou de l'Algérie
        (USA, Irlande). Dans ce cas, des garanties contractuelles standard (Clauses Contractuelles Types)
        sont en place pour assurer un niveau de protection équivalent.
      </p>

      <h2>9. Vos droits</h2>
      <p>Vous disposez des droits suivants sur vos données personnelles :</p>
      <ul>
        <li><strong>Droit d'accès</strong> : obtenir une copie de vos données.</li>
        <li><strong>Droit de rectification</strong> : corriger des données inexactes.</li>
        <li><strong>Droit à l'effacement</strong> ("droit à l'oubli") sous réserve des obligations légales.</li>
        <li><strong>Droit à la limitation</strong> du traitement.</li>
        <li><strong>Droit à la portabilité</strong> : récupérer vos données dans un format structuré.</li>
        <li><strong>Droit d'opposition</strong> au traitement.</li>
        <li><strong>Droit de retirer votre consentement</strong> à tout moment (pour les traitements basés sur le consentement).</li>
      </ul>
      <p>
        Pour exercer ces droits, contactez-nous à{' '}
        <a href="mailto:aatvgeste@gmail.com">aatvgeste@gmail.com</a> en précisant la nature de votre demande.
        Une réponse sera apportée dans un délai maximum de <strong>30 jours</strong>.
      </p>

      <h2>10. Mesures de sécurité</h2>
      <ul>
        <li>Chiffrement HTTPS (TLS 1.2+) pour toutes les communications.</li>
        <li>Mots de passe stockés en bcrypt (haché, jamais en clair).</li>
        <li>Architecture multi-tenant avec isolation stricte vérifiée (audit Feb 2026 — 100 % isolation).</li>
        <li>Rate limiting anti-brute-force sur les endpoints sensibles.</li>
        <li>Sauvegardes chiffrées quotidiennes.</li>
        <li>Conformité <strong>OWASP Top 10</strong>.</li>
        <li>Journal d'audit immuable (hash chain) pour les opérations financières.</li>
      </ul>

      <h2>11. Cookies</h2>
      <p>
        TripNova utilise uniquement des <strong>cookies fonctionnels strictement nécessaires</strong> :
      </p>
      <ul>
        <li><code>access_token</code> et <code>refresh_token</code> : authentification (HttpOnly, sécurisés).</li>
        <li><code>__cf_bm</code> : protection anti-bot Cloudflare.</li>
      </ul>
      <p>
        Aucun cookie publicitaire, aucun cookie de tracking tiers, aucun pixel marketing.
      </p>

      <h2>12. Violation de données</h2>
      <p>
        En cas de violation de données susceptible d'engendrer un risque pour vos droits et libertés,
        l'Éditeur s'engage à :
      </p>
      <ul>
        <li>Notifier l'autorité compétente (ANPDP en Algérie, CNIL équivalent) dans les 72 heures.</li>
        <li>Informer les utilisateurs concernés sans délai injustifié.</li>
      </ul>

      <h2>13. Modifications</h2>
      <p>
        La présente Politique peut être modifiée. Les modifications substantielles font l'objet d'une
        notification par email aux utilisateurs.
      </p>

      <h2>14. Contact et réclamations</h2>
      <p>
        Pour toute question, exercice de vos droits, ou réclamation :{' '}
        <a href="mailto:aatvgeste@gmail.com">aatvgeste@gmail.com</a>
      </p>
      <p>
        Vous avez également le droit d'introduire une réclamation auprès de l'<strong>Autorité Nationale
        de Protection des Données à caractère Personnel</strong> (ANPDP — Algérie) ou de toute autre
        autorité de protection compétente.
      </p>
    </LegalPage>
  );
}
